import { useState, type MouseEvent } from 'react';
import { Box, Button, Menu, MenuItem, ListItemText, Divider, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useT } from '../i18n/useT.js';
import { useActiveProject } from '../lib/activeProject.js';

/**
 * Which project you are looking at, chosen once in the header.
 *
 * It used to be a selector on each screen that needed one, which meant the
 * same question answered a dozen times in a dozen places — and, because every
 * screen kept its own answer, answered differently depending on where you
 * were standing. Here it reads as what it is: the thing all the numbers below
 * are about.
 *
 * Styled as a chip rather than a form field: a header input reads as something
 * you must fill in, while this is a statement of where you are that happens to
 * be clickable. It is deliberately heavier than the toggles beside it, because
 * it changes what every number on the page means and they do not.
 */
export function HeaderProjectPicker({ orgId }: { orgId?: string }) {
  const t = useT().common;
  const { selected, setProjectId, projects } = useActiveProject(orgId);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // Nothing to choose between: one project is the answer, and an empty
  // organization has not got one yet.
  if (projects.length < 2) return null;

  const current = projects.find((p) => p.id === selected);
  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const pick = (id: string) => {
    setProjectId(id);
    setAnchor(null);
  };

  return (
    <>
      <Button
        onClick={open}
        color="inherit"
        startIcon={<FolderOutlinedIcon sx={{ fontSize: 19 }} />}
        endIcon={<ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 15,
          maxWidth: { xs: 150, md: 280 },
          minWidth: 0,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          color: 'text.primary',
          // It carries the whole screen's meaning, so it should not read as
          // one more icon button in a row of them: a tinted, bordered chip
          // with the accent on the folder, sitting apart from the toggles.
          border: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          '& .MuiButton-startIcon': { color: 'primary.main', mr: 0.75 },
          '& .MuiButton-endIcon': { ml: 0.5, color: 'text.secondary' },
          '&:hover': { bgcolor: 'action.selected', borderColor: 'primary.main' },
        }}
      >
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {current?.name ?? t.allProjects}
        </Box>
      </Button>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, maxHeight: 420 } } }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 700, letterSpacing: 0.4 }}
        >
          {t.project.toUpperCase()}
        </Typography>
        {/*
          "All projects" is a real answer on the screens that can show more
          than one at a time; the ones that cannot resolve it to a single
          project themselves, so offering it here never strands them.
        */}
        <MenuItem selected={!selected} onClick={() => pick('')}>
          <ListItemText primary={t.allProjects} />
          {!selected && <CheckIcon fontSize="small" color="primary" />}
        </MenuItem>
        <Divider />
        {projects.map((p) => (
          <MenuItem key={p.id} selected={p.id === selected} onClick={() => pick(p.id)}>
            <ListItemText primary={p.name} />
            {p.id === selected && <CheckIcon fontSize="small" color="primary" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
